// ============================================================================
// TemplatesPage — route /app/templates  (namespace: "templates")
// ----------------------------------------------------------------------------
// Reusable workout templates (Programs) a trainer can apply to any session in
// one tap: list, create, name/reorder, edit and delete. Each program is a named
// set of exercises with default sets/reps/weight per item.
//
// Data flows through the typed React Query hooks (useTemplates / useTemplate /
// useCreateTemplate / useUpdateTemplate / useDeleteTemplate); the AppShell's
// live-sync socket invalidates qk.templates.* so the list stays fresh across
// devices. Optional-first throughout; mobile-first (390 first) with rich,
// reduced-motion-aware motion.
//
// Routing + i18n are pre-wired (see app/router.tsx, i18n/config.ts); this file
// stays a DEFAULT export named TemplatesPage and owns the "templates" i18n
// namespace + everything inside pages/templates/.
// ============================================================================
import { useMemo, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Button, EmptyState } from "../../components";
import { LoadingBlock } from "../../app/PageState";
import { useDeleteTemplate, useTemplates } from "../../hooks/templates";
import { useExercises } from "../../hooks/exercises";
import type { Exercise, Template } from "../../api";
import { TemplateCard } from "./TemplateCard";
import { TemplateEditor } from "./TemplateEditor";
import { ConfirmDialog } from "./ConfirmDialog";
import { IconPlus } from "./icons";

export default function TemplatesPage() {
  const { t } = useTranslation("templates");
  const reduce = useReducedMotion();

  const templatesQuery = useTemplates();
  const catalogQuery = useExercises();
  const del = useDeleteTemplate();

  const catalog = useMemo(() => {
    const map = new Map<number, Exercise>();
    for (const ex of catalogQuery.data ?? []) map.set(ex.id, ex);
    return map;
  }, [catalogQuery.data]);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<Template | undefined>(undefined);
  const [toDelete, setToDelete] = useState<Template | null>(null);

  const templates = templatesQuery.data ?? [];

  const openNew = () => {
    setEditing(undefined);
    setEditorOpen(true);
  };
  const openEdit = (tpl: Template) => {
    setEditing(tpl);
    setEditorOpen(true);
  };
  const closeEditor = () => setEditorOpen(false);

  const confirmDelete = () => {
    if (!toDelete) return;
    del.mutate(toDelete.id, {
      onSuccess: () => {
        setToDelete(null);
      },
    });
  };

  const fade = (delay = 0) => ({
    initial: reduce ? false : { opacity: 0, y: 14 },
    animate: { opacity: 1, y: 0 },
    transition: {
      duration: 0.4,
      delay: reduce ? 0 : delay,
      ease: [0.22, 0.61, 0.36, 1] as const,
    },
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.header
        {...fade(0)}
        className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between"
      >
        <div>
          <p className="text-xs font-bold uppercase tracking-kicker text-coral">
            {t("kicker")}
          </p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tightest text-body">
            {t("title")}
          </h1>
          <p className="mt-1.5 max-w-xl text-sm text-body-soft">{t("subtitle")}</p>
        </div>
        {templates.length > 0 && (
          <Button
            onClick={openNew}
            iconLeft={<IconPlus width={18} height={18} />}
            className="shrink-0"
          >
            {t("actions.new")}
          </Button>
        )}
      </motion.header>

      {/* States */}
      {templatesQuery.isLoading ? (
        <LoadingBlock label={t("loading")} />
      ) : templatesQuery.isError ? (
        <EmptyState
          title={t("error.title")}
          description={t("error.blurb")}
          action={
            <Button variant="ghost" onClick={() => void templatesQuery.refetch()}>
              {t("actions.retry")}
            </Button>
          }
        />
      ) : templates.length === 0 ? (
        <motion.div {...fade(0.05)}>
          <EmptyState
            title={t("empty.title")}
            description={t("empty.blurb")}
            action={
              <Button onClick={openNew} iconLeft={<IconPlus width={18} height={18} />}>
                {t("empty.cta")}
              </Button>
            }
          />
        </motion.div>
      ) : (
        <motion.div
          {...fade(0.05)}
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3"
        >
          <AnimatePresence initial={false}>
            {templates.map((tpl, i) => (
              <motion.div
                key={tpl.id}
                layout={!reduce}
                initial={reduce ? false : { opacity: 0, y: 16, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.96 }}
                transition={{
                  duration: 0.35,
                  delay: reduce ? 0 : Math.min(i * 0.05, 0.3),
                  ease: [0.22, 0.61, 0.36, 1],
                }}
              >
                <TemplateCard
                  template={tpl}
                  catalog={catalog}
                  onEdit={() => openEdit(tpl)}
                  onDelete={() => setToDelete(tpl)}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      )}

      {/* Editor (create / edit) */}
      <TemplateEditor open={editorOpen} onClose={closeEditor} template={editing} />

      {/* Delete confirm */}
      <ConfirmDialog
        open={toDelete !== null}
        title={t("remove.title")}
        body={t("remove.blurb", {
          name: toDelete?.name?.trim() || t("card.untitled"),
        })}
        confirmLabel={t("remove.confirm")}
        cancelLabel={t("remove.cancel")}
        busy={del.isPending}
        error={del.isError ? t("remove.error") : undefined}
        onConfirm={confirmDelete}
        onClose={() => {
          if (!del.isPending) {
            setToDelete(null);
            del.reset();
          }
        }}
      />
    </div>
  );
}
